import React, { JSX } from 'react';
import Styles from '../ProductDetailsV3/CashBackV2/CashbackV2.module.scss';
import ToolTip from '../ToolTip/ToolTip';

interface StaticProductCashbackProps {
  /** Full offer-API response array for this product (uniqueProductData) */
  uniqueProductData: any[];
  /** Top-level CMS content (mainContent from productTableV3) */
  mainContent: any;
}

/**
 * Renders the cashback + rewards-points section for a product card.
 *
 * Visibility logic:
 *  - The offer API must return `promotionInfo[0].cashBack` (the cashback amount).
 *  - The CMS must have `cashBackSection[0]` configured on the productTableV3 block.
 *  - If either is absent nothing is rendered.
 *
 * Title placeholders (both optional):
 *  {0} → replaced with promotionInfo[0].cashBack  (e.g. "100")
 *  {1} → replaced with rewards.points             (e.g. "500")
 *
 * Example CMS title: "{0}.– Rewards bonus (using {1} points)"
 * Renders as:        "100.- Rewards bonus (using 500 points)"
 *
 * If rewards.points is absent the {1} placeholder is simply removed.
 *
 * Keeping this self-contained makes it easy to remove the entire feature by
 * deleting this file and its single usage in StaticProduct.
 */
const StaticProductCashback = ({
  uniqueProductData,
  mainContent
}: StaticProductCashbackProps): JSX.Element | null => {
  // cashBack lives on the bundled internet offer's promotionInfo, not the root product
  const cashbackAmount =
    uniqueProductData?.[0]?.promotionInfo?.[0]?.cashBack ??
    uniqueProductData?.[0]?.bundledOffers
      ?.flatMap((o: any) => o?.promotionInfo ?? [])
      ?.find((p: any) => p?.cashBack)?.cashBack;
  const section = mainContent?.cashBackSection?.[0];

  if (!cashbackAmount || !section) return null;

  const points: string | undefined = uniqueProductData?.[0]?.rewards?.points;
  const numCashback = Number(cashbackAmount);
  const formattedCashback = Number.isInteger(numCashback) 
    ? Math.floor(numCashback)
    : numCashback;

  const title = (section?.title ?? '')
    .replace('{0}', String(formattedCashback))
    .replace('{1}', points ?? '');

  return (
    <div>
      <div className={`${Styles.cashbackcontainer} cashbackv2container`}>
        <div className={Styles.cashbackmodule}>
          <div>
            <span className={Styles.cashbackicon}>
              <img src={section?.icon?.filename} alt='cashback' width={24} height={24} />
            </span>
            <span className={Styles.cashbacktitle}>{title}</span>
            <span className={Styles.cashbacktooltip}>
              {section?.tooltiptext && <ToolTip content={section?.tooltiptext} />}
            </span>
          </div>
          <div className={Styles.cashbackimage}>
            <img src={section?.image?.filename} alt='cashback' width={64} height={64} />
            <span className={Styles.cashbackdescription}>{section?.description}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaticProductCashback;

